import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { LoadScript } from '@reckon-web/load-script';

// ref: https://developers.google.com/recaptcha/docs/invisible#render_param
// ref: https://developers.google.com/recaptcha/docs/display#render_param
type RecaptchaRenderContainer = string | HTMLElement | null;

type RecaptchaRenderParameters = {
  sitekey: string;
  badge?: 'bottomright' | 'bottomleft' | 'inline';
  size?: 'normal' | 'compact' | 'invisible';
  theme?: 'dark' | 'light';
  callback?: (recaptchaResponse: string) => void;
  'expired-callback'?: (recaptchaResponse: string) => void;
  'error-callback'?: (recaptchaResponse: string) => void;
};

declare global {
  interface Window {
    // ref: https://developers.google.com/recaptcha/docs/invisible#js_api
    grecaptcha?: {
      ready: (fn: VoidFunction) => void;
      render: (
        container: RecaptchaRenderContainer,
        params: RecaptchaRenderParameters
      ) => number;
      execute: (widgetId?: number) => void;
      reset: (widgetId?: number) => void;
      getResponse: (widgetId?: number) => void;
    };
  }
}

const RECAPTCHA_SCRIPT_URL =
  'https://www.google.com/recaptcha/api.js?render=explicit';

type UseRecaptchaProps = {
  containerRef: RefObject<RecaptchaRenderContainer>;
  sitekey: RecaptchaRenderParameters['sitekey'];
  size?: RecaptchaRenderParameters['size'];
  theme?: RecaptchaRenderParameters['theme'];
  badge?: RecaptchaRenderParameters['badge'];
  onSuccess?: RecaptchaRenderParameters['callback'];
  onExpired?: RecaptchaRenderParameters['expired-callback'];
  onError?: RecaptchaRenderParameters['error-callback'];
};
/**
 * Loads google recaptcha and tracks the resolved token and if recaptcha is disabled
 */
export const useRecaptcha = ({
  containerRef,
  sitekey,
  badge,
  theme,
  size,
  onError,
  onExpired,
  onSuccess,
}: UseRecaptchaProps) => {
  const [recaptchaToken, setRecaptchaToken] = useState<string>();
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(
    window?.grecaptcha?.render ? true : false
  );
  const [recaptchaWidget, setRecaptchaWidget] = useState<number | null>(null);

  const execute = useCallback(() => {
    if (recaptchaWidget !== null && window.grecaptcha) {
      window.grecaptcha.execute(recaptchaWidget);
    }
  }, [recaptchaWidget]);

  const reset = useCallback(() => {
    if (recaptchaWidget !== null && window.grecaptcha) {
      window.grecaptcha.reset(recaptchaWidget);
    }
  }, [recaptchaWidget]);

  const load = useCallback(async () => {
    if (recaptchaLoaded) {
      return;
    }

    await LoadScript({
      id: 'google-recaptcha',
      parent: 'head',
      src: RECAPTCHA_SCRIPT_URL,
    });
    setRecaptchaLoaded(() => {
      return true;
    });
  }, [recaptchaLoaded]);

  const mount = useCallback(() => {
    if (!window.grecaptcha?.render) {
      return;
    }
    if (!containerRef?.current) {
      return;
    }
    if (recaptchaWidget !== null) {
      return;
    }

    const widget = window.grecaptcha.render(containerRef.current, {
      sitekey,
      size,
      theme,
      badge,
      callback: (token: string) => {
        setRecaptchaToken(token);
        if (typeof onSuccess === 'function') {
          onSuccess(token);
        }
      },
      'expired-callback': (response) => {
        setRecaptchaToken('');
        if (typeof onExpired === 'function') {
          onExpired(response);
        }
      },
      'error-callback': (response) => {
        setRecaptchaToken('');
        if (typeof onError === 'function') {
          onError(response);
        }
      },
    });
    setRecaptchaWidget(widget);
  }, [
    badge,
    containerRef,
    onError,
    onExpired,
    onSuccess,
    recaptchaWidget,
    sitekey,
    size,
    theme,
  ]);

  /**
   * if skipping, bail do nothing.
   *
   * if captcha not loaded, then start loading it.
   *
   * otherwise try to mount.
   *
   * on unload, reset the widget
   */
  useEffect(() => {
    if (!recaptchaLoaded) {
      load();
      return;
    }

    if (!!window.grecaptcha?.ready) {
      window.grecaptcha.ready(() => {
        mount();
      });
    }
  }, [load, mount, recaptchaLoaded, reset]);

  return {
    recaptchaToken,
    recaptchaLoaded,
    recaptchaWidget,
    execute,
    reset,
  };
};
